import { useState, useEffect, useRef, useMemo } from 'react';
import { api } from '../../api/client';
import RenderModelViewer from '../RenderModelViewer';
import IABlockRenderer from '../blocks/IABlockRenderer';
import RewindControl from '../RewindControl';

/**
 * ConfigDrivenDocViewer - Config-driven tabbed document viewer.
 *
 * Reads tab structure from renderModel.rendering_config.detail_html (ADR-054)
 * and routes render model sections to tabs using information_architecture binds.
 *
 * Level 2 IA binds (with render_as) are dispatched to IABlockRenderer.
 * Level 1 IA binds (plain strings) fall back to RawFieldRenderer.
 *
 * WS-IA-003: Replaces TechnicalArchitectureViewer. Studio panels removed.
 */

/** Download dropdown for config-driven document viewer. */
function ConfigDocDownload({ projectId, projectCode, displayId }) {
    const [open, setOpen] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        function close(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    const handleClick = async (mode) => {
        setDownloading(mode);
        try {
            const blob = await api.renderDocument(projectId, displayId, { mode });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const suffix = mode === 'evidence' ? '-evidence' : '';
            a.download = `${projectCode || projectId}-${displayId}${suffix}.md`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            if (err.status === 409) {
                alert(`Render blocked: ${err.data?.message || 'IA verification failed'}`);
            } else {
                console.error('Download failed:', err);
            }
        } finally { setDownloading(null); setOpen(false); }
    };

    return (
        <div ref={ref} className="relative" style={{ flexShrink: 0, marginLeft: 12 }}>
            <button
                onClick={() => setOpen(!open)}
                className="p-2 rounded-lg hover:opacity-80 transition-colors"
                style={{ color: 'var(--text-muted)' }}
                title="Download Markdown"
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
            </button>
            {open && (
                <div
                    className="absolute right-0 top-full mt-1 z-50 rounded-lg border shadow-lg py-1"
                    style={{ background: 'var(--bg-panel)', borderColor: 'var(--border-panel)', minWidth: 220 }}
                >
                    <button
                        onClick={() => handleClick('standard')}
                        disabled={!!downloading}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-white/10 transition-colors"
                        style={{ color: 'var(--text-primary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                    >
                        {downloading === 'standard' ? 'Downloading...' : 'Download Markdown'}
                    </button>
                    <button
                        onClick={() => handleClick('evidence')}
                        disabled={!!downloading}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-white/10 transition-colors"
                        style={{ color: 'var(--text-primary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                    >
                        {downloading === 'evidence' ? 'Downloading...' : 'Download Markdown (With Evidence)'}
                    </button>
                </div>
            )}
        </div>
    );
}

/** Normalize a bind: string -> {path: str}, object passes through. */
function normalizeBind(bind) {
    if (typeof bind === 'string') return { path: bind };
    return bind;
}

export default function ConfigDrivenDocViewer({
    renderModel,
    projectId,
    projectCode,
    docTypeId,
    executionId,
    docTypeName,
    onClose,
    inline,
    nextStepLabel,
    onProduceNext,
}) {
    const [activeTab, setActiveTab] = useState(null);

    // Build section-to-tab mapping from rendering config + information architecture
    const { tabDefs, tabSections, isRawContentMode } = useMemo(() => {
        const detail_html = renderModel?.rendering_config?.detail_html;
        const ia = renderModel?.information_architecture;
        const sections = renderModel?.sections || [];
        const rawContent = renderModel?.raw_content;

        if (!detail_html?.tabs || !ia?.sections) {
            // Fallback: show all sections in a single overview tab
            return {
                tabDefs: [{ id: 'overview', label: 'Overview', sections: [] }],
                tabSections: { overview: sections },
                isRawContentMode: false,
            };
        }

        // Build map: IA section id -> { binds: [...normalized...], label }
        const sectionMeta = {};
        ia.sections.forEach(s => {
            sectionMeta[s.id] = {
                binds: (s.binds || []).map(normalizeBind),
                label: s.label || s.id,
            };
        });

        // Prefer IA-driven raw content mode when raw_content + IA config are available.
        if (rawContent && typeof rawContent === 'object') {
            // Collect all bound field paths across all IA sections
            const allBindPaths = new Set();
            Object.values(sectionMeta).forEach(meta =>
                meta.binds.forEach(b => allBindPaths.add(b.path))
            );
            const hasIAFields = [...allBindPaths].some(
                p => rawContent[p] !== undefined && rawContent[p] !== null
            );

            if (hasIAFields) {
                // Raw content mode: build tabSections as arrays of {fieldName, label, data, bind}
                const grouped = {};
                detail_html.tabs.forEach(tab => {
                    const fields = [];
                    (tab.sections || []).forEach(sectionId => {
                        const meta = sectionMeta[sectionId];
                        if (!meta) return;
                        meta.binds.forEach(bind => {
                            const data = rawContent[bind.path];
                            if (data !== undefined && data !== null) {
                                fields.push({
                                    fieldName: bind.path,
                                    label: meta.label,
                                    data,
                                    bind,
                                });
                            }
                        });
                    });
                    grouped[tab.id] = fields;
                });
                return { tabDefs: detail_html.tabs, tabSections: grouped, isRawContentMode: true };
            }

            // No IA-bound fields — extract fallback text from envelope or show raw content
            const fallbackText = rawContent.raw === true && typeof rawContent.content === 'string'
                ? rawContent.content
                : null;
            if (fallbackText) {
                const firstTab = detail_html.tabs[0]?.id || 'overview';
                const grouped = {};
                detail_html.tabs.forEach(tab => { grouped[tab.id] = []; });
                grouped[firstTab] = [{ fieldName: '_fallback', label: 'Content', data: fallbackText, bind: { path: '_fallback' } }];
                return { tabDefs: detail_html.tabs, tabSections: grouped, isRawContentMode: true };
            }
        }

        // Standard mode: route render model sections to tabs via field binds
        const fieldToTab = {};
        detail_html.tabs.forEach(tab => {
            (tab.sections || []).forEach(sectionId => {
                const meta = sectionMeta[sectionId];
                if (meta) {
                    meta.binds.forEach(bind => { fieldToTab[bind.path] = tab.id; });
                }
            });
        });

        const grouped = {};
        detail_html.tabs.forEach(tab => { grouped[tab.id] = []; });

        sections.forEach(section => {
            const sid = section.section_id;
            const tabId = fieldToTab[sid] || 'overview';
            if (grouped[tabId]) {
                grouped[tabId].push(section);
            } else if (grouped.overview) {
                grouped.overview.push(section);
            }
        });

        return { tabDefs: detail_html.tabs, tabSections: grouped, isRawContentMode: false };
    }, [renderModel]);

    // Default to first tab
    const effectiveTab = activeTab || (tabDefs.length > 0 ? tabDefs[0].id : 'overview');

    // Build tab list with counts and disabled state
    const tabs = useMemo(() => {
        return tabDefs.map(tab => {
            const tabData = tabSections[tab.id] || [];

            if (isRawContentMode) {
                // Show actual item count: if tab has a single array field,
                // show the length of that array (e.g., 5 WP candidates),
                // not the number of IA binds (which is always 1 per section).
                let count = null;
                if (tabData.length === 1 && Array.isArray(tabData[0]?.data)) {
                    count = tabData[0].data.length;
                } else if (tabData.length > 1) {
                    count = tabData.length;
                }
                return {
                    id: tab.id,
                    label: tab.label,
                    count,
                    disabled: tabData.length === 0,
                };
            }

            // Standard mode
            const blockCount = tabData[0]?.blocks?.length || null;
            return {
                id: tab.id,
                label: tab.label,
                count: blockCount,
                disabled: tab.id !== 'overview' && !tabData.length,
            };
        });
    }, [tabDefs, tabSections, isRawContentMode]);

    const metadata = renderModel?.metadata || {};
    const adminUrl = executionId ? `/admin?execution=${executionId}` : '/admin';

    const displayTitle = (() => {
        const t = renderModel?.title || 'Document';
        const colonIndex = t.indexOf(': ');
        return colonIndex > -1 ? t.slice(colonIndex + 2) : t;
    })();

    const formatDate = (iso) => {
        if (!iso) return null;
        try {
            const d = new Date(iso);
            return d.toLocaleDateString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        } catch { return null; }
    };

    const generatedDate = formatDate(metadata.created_at);
    const updatedDate = formatDate(metadata.updated_at);
    const lifecycleState = metadata.lifecycle_state;

    // Build filtered renderModel for a given tab
    const buildTabRenderModel = (tabId) => {
        if (!renderModel) return null;
        const sections = tabSections[tabId] || [];
        return { ...renderModel, sections };
    };

    return (
        <div className="flex flex-col h-full" style={{ background: 'var(--bg-canvas)' }}>
            {/* Document header */}
            <div
                className="px-5 py-3 border-b"
                style={{ background: 'var(--bg-panel)', borderColor: 'var(--border-panel)' }}
            >
                <div className="flex items-start justify-between">
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 4 }}>
                            {projectCode && (
                                <a
                                    href={adminUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={executionId ? `View execution ${executionId}` : 'Open Admin Executions'}
                                    style={{
                                        padding: '2px 8px',
                                        background: '#10b981',
                                        color: 'white',
                                        fontSize: 11,
                                        fontWeight: 600,
                                        borderRadius: 4,
                                        letterSpacing: '0.05em',
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {projectCode}
                                </a>
                            )}
                            <span style={{
                                padding: '2px 8px',
                                background: '#eef2ff',
                                color: '#4f46e5',
                                fontSize: 11,
                                fontWeight: 600,
                                borderRadius: 4,
                            }}>
                                {docTypeName || docTypeId?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Document'}
                            </span>
                            {lifecycleState && (
                                <span style={{
                                    padding: '2px 8px',
                                    background: lifecycleState === 'complete' ? '#dcfce7' : '#fef3c7',
                                    color: lifecycleState === 'complete' ? '#166534' : '#92400e',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    borderRadius: 4,
                                }}>
                                    {lifecycleState}
                                </span>
                            )}
                        </div>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                            {displayTitle}
                        </h2>
                        {generatedDate && (
                            <div style={{ marginTop: 2, fontSize: 12, color: 'var(--text-dim)' }}>
                                Generated {generatedDate}
                                {updatedDate && updatedDate !== generatedDate && (
                                    <span> &middot; Updated {updatedDate}</span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
                        {/* ADR-063/069: Rewind control with correction pre-population */}
                        {projectId && docTypeId && (
                            <RewindControl
                                projectId={projectId}
                                stageName={docTypeId}
                                displayName={docTypeId.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                onRewindComplete={() => {
                                    window.location.hash = `stage=${docTypeId}`;
                                    window.location.reload();
                                }}
                            />
                        )}
                        {/* Produce next document CTA */}
                        {nextStepLabel && onProduceNext && (
                            <button
                                className="px-3 py-1.5 rounded-lg font-semibold transition-all hover:brightness-110"
                                style={{
                                    fontSize: 11,
                                    backgroundColor: 'var(--state-ready-bg)',
                                    color: 'white',
                                    whiteSpace: 'nowrap',
                                }}
                                onClick={onProduceNext}
                            >
                                Produce {nextStepLabel} &rarr;
                            </button>
                        )}
                        {/* Download Markdown dropdown */}
                        {metadata.display_id && projectId && (
                            <ConfigDocDownload
                                projectId={projectId}
                                projectCode={projectCode}
                                displayId={metadata.display_id}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Tab bar */}
            <div
                className="flex items-center gap-1 px-4 border-b"
                style={{
                    background: 'var(--bg-canvas)',
                    borderColor: 'var(--border-panel)',
                    minHeight: 40,
                }}
            >
                {tabs.map(tab => {
                    const isActive = effectiveTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => !tab.disabled && setActiveTab(tab.id)}
                            disabled={tab.disabled}
                            className="relative px-4 py-2 text-sm font-medium transition-colors"
                            style={{
                                color: isActive ? 'var(--action-primary)' : tab.disabled ? 'var(--text-dim)' : 'var(--text-muted)',
                                background: 'transparent',
                                border: 'none',
                                cursor: tab.disabled ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {tab.label}
                            {tab.count !== null && (
                                <span
                                    className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full"
                                    style={{
                                        background: isActive ? 'var(--bg-selected)' : 'var(--bg-button)',
                                        color: isActive ? 'var(--action-primary)' : 'var(--text-muted)',
                                    }}
                                >
                                    {tab.count}
                                </span>
                            )}
                            {isActive && (
                                <div
                                    className="absolute bottom-0 left-0 right-0 h-0.5"
                                    style={{ background: 'var(--action-primary)' }}
                                />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Tab content - key forces remount so sections start expanded */}
            <div
                key={effectiveTab}
                className="flex-1 overflow-hidden"
                style={{ minHeight: 0 }}
            >
                {isRawContentMode ? (
                    <RawContentTabContent fields={tabSections[effectiveTab] || []} />
                ) : (
                    <SectionTabContent
                        renderModel={buildTabRenderModel(effectiveTab)}
                        sections={tabSections[effectiveTab] || []}
                    />
                )}
            </div>
        </div>
    );
}

/** Generic tab content that renders sections via RenderModelViewer */
function SectionTabContent({ renderModel, sections }) {
    return (
        <div
            className="h-full overflow-y-auto p-6"
            onWheel={(e) => e.stopPropagation()}
        >
            {renderModel && sections.length > 0 ? (
                <RenderModelViewer
                    renderModel={renderModel}
                    variant="full"
                    hideHeader={true}
                />
            ) : (
                <div className="text-center py-12 text-gray-500">
                    No content available for this tab
                </div>
            )}
        </div>
    );
}

/** Raw content tab - renders fields pulled from raw_content via IA binds.
 *  Level 2 binds (with render_as) dispatch to IABlockRenderer.
 *  Level 1 binds (no render_as) fall back to RawFieldRenderer.
 */
function RawContentTabContent({ fields }) {
    if (!fields || fields.length === 0) {
        return (
            <div className="h-full overflow-y-auto p-6" onWheel={(e) => e.stopPropagation()}>
                <div className="text-center py-12 text-gray-500">
                    No content available for this tab
                </div>
            </div>
        );
    }
    return (
        <div className="h-full overflow-y-auto p-6 space-y-6" onWheel={(e) => e.stopPropagation()}>
            {fields.map((field, i) => (
                <div key={field.fieldName || i} style={{ background: 'var(--bg-node)', border: '1px solid var(--border-node)', borderRadius: 8 }}>
                    <div className="flex items-center gap-2" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-node)' }}>
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                            {field.label || formatFieldLabel(field.fieldName)}
                        </h3>
                        {Array.isArray(field.data) && (
                            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-dim)' }}>
                                {field.data.length}
                            </span>
                        )}
                    </div>
                    <div style={{ padding: 16 }}>
                        {field.bind?.render_as ? (
                            <IABlockRenderer bind={field.bind} data={field.data} label={field.label} />
                        ) : (
                            <RawFieldRenderer data={field.data} fieldName={field.fieldName} />
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

/** Dispatches rendering by data type: strings, arrays, objects, fallback JSON.
 *  Used for Level 1 binds (no render_as declared).
 */
function RawFieldRenderer({ data, fieldName }) {
    if (data === null || data === undefined) {
        return <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>No data</span>;
    }
    if (typeof data === 'string') {
        return <p style={{ fontSize: 14, color: 'var(--text-primary)', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{data}</p>;
    }
    if (Array.isArray(data)) {
        if (data.length === 0) {
            return <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>Empty list</span>;
        }
        return (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }} className="space-y-2">
                {data.map((item, i) => (
                    <li key={typeof item === 'object' ? (item?.id || i) : i} className="flex items-start gap-2" style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                        <span style={{ color: 'var(--text-muted)', marginTop: 2, flexShrink: 0, fontSize: 12 }}>{'\u2022'}</span>
                        <div style={{ flex: 1 }}>
                            {typeof item === 'string' ? (
                                <span>{item}</span>
                            ) : typeof item === 'object' && item !== null ? (
                                <RawStructuredItem item={item} />
                            ) : (
                                <span>{String(item)}</span>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        );
    }
    if (typeof data === 'object') {
        return (
            <div className="space-y-3">
                {Object.entries(data).map(([k, v]) => (
                    <div key={k} style={{ borderLeft: '3px solid var(--border-node)', paddingLeft: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                            {formatFieldLabel(k)}
                        </div>
                        <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>
                            <RawObjectFieldValue value={v} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    // Fallback
    return (
        <pre style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
            {JSON.stringify(data, null, 2)}
        </pre>
    );
}

/** Smart field extraction for structured items (array-of-objects) */
function RawStructuredItem({ item }) {
    // Detect primary text field
    const textKeys = ['constraint', 'assumption', 'guardrail', 'recommendation', 'description',
                      'question', 'text', 'statement', 'name', 'title', 'problem_understanding',
                      'architectural_intent', 'proposed_system_shape'];
    const textKey = textKeys.find(k => item[k] && typeof item[k] === 'string');
    const text = textKey ? item[textKey] : null;

    // Badge-worthy metadata
    const id = item.id;
    const confidence = item.confidence;
    const likelihood = item.likelihood;
    const constraintType = item.constraint_type;

    // Secondary fields
    const why = item.why_it_matters || item.why_early;
    const impact = item.impact_on_planning || item.impact_if_unresolved || item.impact;
    const mitigation = item.mitigation_direction;
    const recommendation = item.recommendation_direction;
    const validation = item.validation_approach;

    if (!text) {
        // No recognizable primary field: show all as inline key-value pairs
        return (
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {Object.entries(item).map(([k, v], i) => (
                    <span key={k}>
                        {i > 0 && ' \u00B7 '}
                        <span style={{ fontWeight: 500 }}>{formatFieldLabel(k)}:</span>{' '}
                        {typeof v === 'string' ? v : JSON.stringify(v)}
                    </span>
                ))}
            </span>
        );
    }

    return (
        <div>
            <div className="flex items-center gap-2 flex-wrap">
                {id && <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-dim)' }}>{id}</span>}
                <span style={{ fontWeight: 500 }}>{text}</span>
                {constraintType && (
                    <span style={{ fontSize: 11, padding: '1px 6px', background: 'var(--bg-button)', borderRadius: 4, color: 'var(--text-muted)' }}>{constraintType}</span>
                )}
                {confidence && (
                    <span style={{
                        fontSize: 11, padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                        background: confidence === 'high' ? '#dcfce7' : confidence === 'medium' ? '#fef3c7' : '#fee2e2',
                        color: confidence === 'high' ? '#166534' : confidence === 'medium' ? '#92400e' : '#991b1b',
                    }}>{confidence}</span>
                )}
                {likelihood && (
                    <span style={{ fontSize: 11, padding: '1px 6px', background: '#fee2e2', borderRadius: 4, color: '#991b1b' }}>
                        {likelihood}{item.impact ? `/${item.impact}` : ''}
                    </span>
                )}
            </div>
            {validation && <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0' }}>Validation: {validation}</p>}
            {why && <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0' }}>Why: {why}</p>}
            {impact && typeof impact === 'string' && <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0' }}>Impact: {impact}</p>}
            {mitigation && <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0' }}>Mitigation: {mitigation}</p>}
            {recommendation && <p style={{ fontSize: 12, color: '#059669', margin: '2px 0 0' }}>Recommendation: {recommendation}</p>}
        </div>
    );
}

/** Render a field value inside an object, handling arrays as lists */
function RawObjectFieldValue({ value }) {
    if (value === null || value === undefined) return <span style={{ color: 'var(--text-dim)' }}>—</span>;
    if (typeof value === 'boolean') return <span>{value ? 'Yes' : 'No'}</span>;
    if (typeof value === 'string') return <span>{value}</span>;
    if (Array.isArray(value)) {
        if (value.length === 0) return <span style={{ color: 'var(--text-dim)' }}>None</span>;
        return (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {value.map((item, i) => (
                    <li key={i} className="flex items-start gap-2" style={{ marginBottom: 4 }}>
                        <span style={{ color: 'var(--text-muted)', marginTop: 2, flexShrink: 0, fontSize: 12 }}>{'\u2022'}</span>
                        <span>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
                    </li>
                ))}
            </ul>
        );
    }
    if (typeof value === 'object') return <span>{JSON.stringify(value, null, 2)}</span>;
    return <span>{String(value)}</span>;
}

/** Convert snake_case field name to Title Case label */
function formatFieldLabel(key) {
    return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
